import { app } from '@packages/app';
import { createContext, stateSetter } from '@packages/context';
import { createActions } from '@packages/context/lib/action';
import { RouterServiceProvider } from '../../router/lib/RouterServiceProvider';
import { mapTabs, searchTabs } from './common';
import { TabState, TabStateType } from './data';

type ActionsType =
    | 'setIndex'
    | 'setTab'
    | 'setTabs'
    | 'setMounted'
    | 'nextTab'
    | 'previousTab'
    | 'setShowTabs'
    | 'setTabChips'
    | 'navigate';

const ACTIONS = createActions<ActionsType>([
    'setIndex',
    'setTab',
    'setTabs',
    'setMounted',
    'nextTab',
    'previousTab',
    'setShowTabs',
    'setTabChips',
    'navigate',
]);

export const { Provider, Context } = createContext<TabStateType, typeof ACTIONS>({
    name: 'Tabs',
    initialState: TabState,
    debug: false,
    stateExtend(state) {
        const router = app(RouterServiceProvider);

        if (state.get('tabs')) {
            state = state.set('tabs', mapTabs(state.get('tabs')));
        }

        if (state.get('url') && router.isDefined(state.get('url'))) {
            state = state.set('url', router.getRouteUrl(state.get('url')));
        }

        return state;
    },
    actions: ACTIONS,
    events: {
        onSetShowTabs: stateSetter('showTabs'),
        onSetTabChips: stateSetter('tabChips'),
        onSetIndex: (state, { payload }) => state.set('index', payload),
        onSetTab: (state, { payload }) => state.set('index', searchTabs(state.get('tabs'), payload)?.index as any),
        onSetTabs: (state, { payload }) => state.set('tabs', mapTabs(payload)),
        onSetMounted: (state, { payload }) =>
            state.set(
                'tabs',
                state.get('tabs').map((tab, id) => {
                    if (id === payload) {
                        tab._mounted = true;
                    }

                    return tab;
                }),
            ),
        onNextTab(state, action, deps) {
            return this.onNavigate(
                state,
                {
                    ...action,
                    payload: Math.min(state.get('index') + 1, state.get('tabs').length - 1),
                },
                deps,
            );
        },
        onPreviousTab(state, action, deps) {
            return this.onNavigate(
                state,
                {
                    ...action,
                    payload: Math.max(0, state.get('index') - 1),
                },
                deps,
            );
        },
        onNavigate(state, { payload }, deps) {
            const url = state.get('url');
            const urlProps = state.get('urlProps');
            const tabs$ = state.get('tabs');
            const idx = payload;

            // Get current tab
            const tab = tabs$[idx];
            const { name } = tab;

            if (url) {
                setTimeout(() => {
                    // Go to the route
                    deps!.router.goTo(url, {
                        ...(urlProps || {}),
                        tab: name,
                    });
                });
            }

            return state.set('index', idx);
        },
    },
});
